import { AxiosError } from "axios";
import { HttpService } from "data/http-service";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import { ActivitiesPageFilters } from "domain/entities/activities.entities";
import { getActivitiesFilters } from "data/activities/get-activities-filters";
import { VisualSupportsGateway } from "domain/visual-supports/visual-supports.gateway";
import { VisualSupport } from "domain/entities/visual-supports.entities";
import {
  visualSupportDtoSchema,
  visualSupportsDtoSchema,
} from "data/visual-supports/visual-supports.dto.schemas";

export class VisualSupportsHttpGateway implements VisualSupportsGateway {
  async getVisualSupports(
    filters: Partial<ActivitiesPageFilters> & Partial<{ search: string }>
  ): Promise<{
    supports: VisualSupport[];
    totalCount: number;
    success: boolean;
  }> {
    return HttpService.get("visuels", { params: getActivitiesFilters(filters) })
      .then((response) => {
        if (response.status === 200) {
          const parsedSupports = visualSupportsDtoSchema.safeParse(
            response.data.data
          );
          if (parsedSupports.success) {
            return {
              supports: parsedSupports.data,
              totalCount: response.data.total,
              success: true,
            };
          } else return { supports: [], totalCount: 0, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:VisualSupports.FindError"),
          });
          return {
            supports: [],
            totalCount: 0,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:VisualSupports.FindError"),
        });
        return {
          supports: [] as VisualSupport[],
          totalCount: 0,
          success: false,
        };
      });
  }

  async getVisualSupport(id: number): Promise<{
    support: VisualSupport | null;
    success: boolean;
  }> {
    return HttpService.get(`visuels/${id}`)
      .then((response) => {
        if (response.status === 200) {
          const parsedSupport = visualSupportDtoSchema.safeParse(response.data);
          if (parsedSupport.success) {
            return {
              support: parsedSupport.data,
              success: true,
            };
          } else return { support: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:VisualSupport.FindError"),
          });
          return {
            support: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:VisualSupport.FindError"),
        });
        return { support: null as VisualSupport, success: false };
      });
  }

  async getFavoritesVisualSupports(supportIds: number[]): Promise<{
    supports: VisualSupport[];
    success: boolean;
  }> {
    const ids = supportIds.length > 0 ? supportIds : [-1];
    return HttpService.get("visuels", { params: { "id[$in]": ids } })
      .then((response) => {
        if (response.status === 200) {
          const parsedSupports = visualSupportsDtoSchema.safeParse(
            response.data.data
          );
          if (parsedSupports.success) {
            return {
              supports: parsedSupports.data,
              success: true,
            };
          } else return { supports: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:VisualSupports.FindError"),
          });
          return {
            supports: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:VisualSupports.FindError"),
        });
        return { supports: [] as VisualSupport[], success: false };
      });
  }

  async getRandomVisualSupport(): Promise<{
    support: VisualSupport | null;
    success: boolean;
  }> {
    return HttpService.get("visuels/random")
      .then((response) => {
        if (response.status === 200) {
          const parsedSupport = visualSupportDtoSchema.safeParse(response.data);
          if (parsedSupport.success) {
            return {
              support: parsedSupport.data,
              success: true,
            };
          } else return { support: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:VisualSupport.FindError"),
          });
          return {
            support: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:VisualSupport.FindError"),
        });
        return { support: null as VisualSupport, success: false };
      });
  }
}
